/**
 * Attempts to dynamically import a component safely, with automatic retries on failure.
 *
 * @template T - The type of the imported component/module.
 * @param importFn - A function that returns a Promise resolving to the component/module.
 * @param retries - Number of times to retry if the import fails (default: 2).
 * @returns A Promise that resolves to the imported component/module.
 *
 * How it works:
 * 1. Calls the import function.
 * 2. If it fails, waits 100ms and tries again, decreasing the remaining retries.
 * 3. Repeats until retries run out.
 * 4. Resolves with the successfully imported component/module.
 *
 * This prevents the app from breaking if a lazy-loaded component temporarily fails to load
 * (for example, due to network issues or module chunk errors).
 */
export const safeLoadComponent = <T>(
   importFn: () => Promise<T>,
   retries = 2
): Promise<T> => {
   const attempt = (remaining: number): Promise<T> =>
      importFn().catch(() => {
         return new Promise<T>((resolve) =>
            setTimeout(() => resolve(attempt(remaining - 1)), 100)
         );
      });

   return attempt(retries);
};